/**
 * stripUndefined — recursively remove `undefined` values from objects and
 * arrays. Firestore rejects writes that contain `undefined` at any depth.
 *
 * Why this exists:
 *   Shallow versions (the various `clean()` helpers scattered across the
 *   codebase) only handle the top-level keys; a nested object like
 *   `{ overrides: { season: undefined } }` slips through and crashes
 *   `updateDoc()` with the same message as a direct undefined value.
 *
 * Preserves:
 *   - `null` (Firestore accepts null)
 *   - `Date` and Firestore `Timestamp` instances (objects with `toDate` or
 *     `toMillis` are returned as-is — never recursed into)
 *   - primitives (number, string, boolean)
 *
 * Strips:
 *   - top-level `undefined` keys
 *   - nested-object `undefined` keys at any depth
 *   - `undefined` values inside arrays (preserves index by skipping? — see
 *     below: we keep the slot by mapping to `null` to avoid array shape
 *     surprises; in practice arrays of objects are the common shape and we
 *     recurse into each element).
 */
export function stripUndefined<T>(value: T): T {
  if (value === undefined || value === null) return value;

  // Preserve Date instances — Firestore SDK serializes Date to Timestamp.
  if (value instanceof Date) return value;

  // Preserve Firestore Timestamp / FieldValue / any object that exposes
  // `toDate` or `toMillis` (Timestamp) or `_methodName` (FieldValue sentinel).
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    if (typeof o.toDate === "function" || typeof o.toMillis === "function") {
      return value;
    }
    // Detect Firestore FieldValue sentinels (serverTimestamp / increment / etc).
    if (typeof (o as { _methodName?: unknown })._methodName === "string") {
      return value;
    }
  }

  if (Array.isArray(value)) {
    return value.map((v) => stripUndefined(v)) as unknown as T;
  }

  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      out[k] = stripUndefined(v);
    }
    return out as T;
  }

  return value;
}
